// A faithful Apple Wallet pass renderer.
//
// ⚠️ MIRROR of flowIQ_v1 `src/components/integrations/passes/WalletPassPreview.tsx`
// (the admin Passes dashboard renderer) with `anyColorToHex` inlined from its
// `passkit.ts`. The flowIQ copy is the source of truth — port changes here so
// popups render passes EXACTLY as the admin dashboard does.
//
// Layout metrics mirror Wallet's real pass anatomy per type (header row,
// strip/thumbnail/transit primaries, combined secondary+auxiliary rows for
// store cards & coupons, separate rows for event tickets, footer image for
// boarding passes, barcode plate, back-of-pass field list).
//
// Interaction model: every element carries an INTERACTIVE OVERLAY (a
// transparent layer rendered on top of the element) instead of styles on the
// element itself. Overlays can never be covered by images or cropped by the
// card's rounded-corner clipping. Hovering shows a soft preview; the designer
// selects exactly ONE element at a time (`selected`), which renders a solid
// frame + a small name chip. (Designer props are unused in the widget but kept
// verbatim so future syncs stay a straight copy.)

import { useMemo } from "react";
import QRCode from "qrcode";

function anyColorToHex(c: string | undefined, fallback: string): string {
  if (!c) return fallback;
  const t = c.trim();
  if (/^#[0-9a-f]{6}$/i.test(t)) return t;
  if (/^#[0-9a-f]{3}$/i.test(t)) {
    return `#${t[1]}${t[1]}${t[2]}${t[2]}${t[3]}${t[3]}`;
  }
  const m = /rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i.exec(t);
  if (m) {
    const to2 = (v: string) => Math.min(255, parseInt(v, 10)).toString(16).padStart(2, "0");
    return `#${to2(m[1])}${to2(m[2])}${to2(m[3])}`;
  }
  return fallback;
}

type AnyJson = Record<string, any>;

export interface PreviewImages {
  icon?: string;
  logo?: string;
  strip?: string;
  background?: string;
  thumbnail?: string;
  footer?: string;
}

interface WalletPassPreviewProps {
  passJson: AnyJson;
  images: PreviewImages;
  values?: Record<string, string | number>;
  face?: "front" | "back";
  width?: number;
  voided?: boolean;
  /** Show dashed hints for empty image slots (designer mode). */
  placeholders?: boolean;
  onAreaClick?: (area: string) => void;
  /** The single selected area id (e.g. "strip", "secondary#1"). */
  selected?: string | null;
  /** Overlay dashed outlines + tiny labels naming each field area. */
  blueprint?: boolean;
}

const STYLE_KEYS = ["storeCard", "coupon", "eventTicket", "boardingPass", "generic"] as const;

function styleOf(passJson: AnyJson): (typeof STYLE_KEYS)[number] {
  return STYLE_KEYS.find((k) => passJson[k]) || "storeCard";
}

// ---------------------------------------------------------------------------
// Value formatting (mirrors PassFieldContent rendering)
// ---------------------------------------------------------------------------

/**
 * Substitute {{token}} placeholders from the pass's live values.
 *
 * The signed .pkpass does this at generation time, so a field like the member
 * number (`value: "{{serial}}"`) or the rewards/invite links renders correctly
 * in Apple Wallet. This preview resolved fields by KEY only, so any field whose
 * token differs from its own key showed the customer a literal "{{serial}}".
 */
function interpolateTokens(text: string, values?: Record<string, string | number>): string {
  if (!values || !text.includes("{{")) return text;
  return text.replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (m, k) =>
    values[k] !== undefined ? String(values[k]) : m
  );
}

/**
 * Pull the href + visible text out of an attributedValue anchor, with tokens
 * substituted. Returns null for anything that is not a single <a> tag.
 */
function anchorFrom(
  attributedValue: unknown,
  values?: Record<string, string | number>
): { href: string; text: string } | null {
  if (typeof attributedValue !== "string") return null;
  const m = attributedValue.match(/<a\s+href=['"]([^'"]+)['"]\s*>([\s\S]*?)<\/a>/i);
  if (!m) return null;
  return { href: interpolateTokens(m[1], values), text: interpolateTokens(m[2], values).trim() };
}

function formatFieldValue(field: AnyJson, values?: Record<string, string | number>): string {
  const rawValue: unknown = values && field.key in values ? values[field.key] : field.value;
  const v: unknown = typeof rawValue === "string" ? interpolateTokens(rawValue, values) : rawValue;
  if (v === undefined || v === null || v === "") return "";

  if (field.currencyCode && !Number.isNaN(Number(v))) {
    try {
      return new Intl.NumberFormat("en-ZA", { style: "currency", currency: String(field.currencyCode) }).format(Number(v));
    } catch {
      return `${field.currencyCode} ${v}`;
    }
  }
  if (field.dateStyle || field.timeStyle) {
    const d = new Date(String(v));
    if (!Number.isNaN(d.getTime())) {
      const dateStyleMap: Record<string, "short" | "medium" | "long" | "full" | undefined> = {
        PKDateStyleShort: "short", PKDateStyleMedium: "medium", PKDateStyleLong: "long", PKDateStyleFull: "full",
      };
      const ds = dateStyleMap[field.dateStyle];
      const ts = dateStyleMap[field.timeStyle];
      try {
        return new Intl.DateTimeFormat("en-ZA", { dateStyle: ds, timeStyle: ts }).format(d);
      } catch {
        return d.toLocaleString();
      }
    }
  }
  if (typeof v === "number") return v.toLocaleString("en-ZA");
  return String(v);
}

function alignClass(field: AnyJson, fallback: string): string {
  switch (field.textAlignment) {
    case "PKTextAlignmentLeft": return "text-left";
    case "PKTextAlignmentCenter": return "text-center";
    case "PKTextAlignmentRight": return "text-right";
    default: return fallback;
  }
}

// ---------------------------------------------------------------------------
// Barcode graphics. The QR is a REAL encoder (see QrGraphic). Aztec / PDF417 /
// linear remain seeded decorative stand-ins — no encoder shipped for them yet.
// ---------------------------------------------------------------------------

function hashSeed(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h || 1;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// The QR is REAL. What a phone camera reads off the web card (`/install`, `/my`,
// the popup's in-card preview, the admin designer) is the pass's own barcode
// message, token-interpolated by the caller — the same payload Wallet encodes into
// the installed pass. Until 28 Aug 2026 this drew finder squares plus seeded noise:
// it looked like a QR and encoded nothing, so a member holding up their dashboard
// could not be scanned (the referral "show your pass" mechanic). Error correction M
// is what Wallet renders. The quiet zone comes from the white plate padding around
// the graphic (8 * s) plus the 1-module margin drawn here.
function qrModules(payload: string): boolean[][] {
  try {
    const qr = QRCode.create(payload || "flowpass", { errorCorrectionLevel: "M" });
    const n = qr.modules.size;
    return Array.from({ length: n }, (_, y) => Array.from({ length: n }, (_, x) => qr.modules.get(y, x) === 1));
  } catch {
    return []; // payload too long for any QR version — draw nothing rather than a code that lies
  }
}

function QrGraphic({ seed, size }: { seed: string; size: number }) {
  const cells = useMemo(() => qrModules(seed), [seed]);
  const n = cells.length;
  const margin = 1; // modules of white inside the graphic, on every side
  const u = n ? size / (n + margin * 2) : 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} shapeRendering="crispEdges" aria-hidden>
      {cells.flatMap((row, y) => row.map((on, x) => (on ? <rect key={`${y}-${x}`} x={(x + margin) * u} y={(y + margin) * u} width={u + 0.2} height={u + 0.2} fill="#111" /> : null)))}
    </svg>
  );
}

function AztecGraphic({ seed, size }: { seed: string; size: number }) {
  const cells = useMemo(() => {
    const n = 23;
    const rng = mulberry32(hashSeed(seed || "flowpass"));
    const grid: boolean[][] = Array.from({ length: n }, () => Array(n).fill(false));
    const c = Math.floor(n / 2);
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
      const d = Math.max(Math.abs(y - c), Math.abs(x - c));
      if (d <= 5) grid[y][x] = d % 2 === 0; // bullseye rings
      else grid[y][x] = rng() < 0.45;
    }
    return grid;
  }, [seed]);
  const n = cells.length;
  const u = size / n;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
      {cells.flatMap((row, y) => row.map((on, x) => (on ? <rect key={`${y}-${x}`} x={x * u} y={y * u} width={u + 0.2} height={u + 0.2} fill="#111" /> : null)))}
    </svg>
  );
}

function Pdf417Graphic({ seed, width, height }: { seed: string; width: number; height: number }) {
  const bars = useMemo(() => {
    const rng = mulberry32(hashSeed(seed || "flowpass"));
    const rows = 7;
    const out: { x: number; y: number; w: number; h: number }[] = [];
    const rowH = height / rows;
    for (let r = 0; r < rows; r++) {
      let x = 0;
      out.push({ x: 0, y: r * rowH, w: width * 0.055, h: rowH }); // start pattern
      x = width * 0.075;
      while (x < width * 0.9) {
        const w = (0.5 + rng() * 2.2) * (width / 90);
        if (rng() < 0.62) out.push({ x, y: r * rowH, w, h: rowH });
        x += w + (width / 110) * (0.5 + rng());
      }
      out.push({ x: width * 0.93, y: r * rowH, w: width * 0.07, h: rowH }); // stop pattern
    }
    return out;
  }, [seed, width, height]);
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      {bars.map((b, i) => <rect key={i} x={b.x} y={b.y + 0.4} width={b.w} height={b.h - 0.8} fill="#111" />)}
    </svg>
  );
}

function LinearGraphic({ seed, width, height, guards }: { seed: string; width: number; height: number; guards?: boolean }) {
  const bars = useMemo(() => {
    const rng = mulberry32(hashSeed(seed || "flowpass"));
    const out: { x: number; w: number; tall: boolean }[] = [];
    let x = guards ? width * 0.02 : 0;
    if (guards) { out.push({ x: 0, w: width * 0.008, tall: true }, { x: width * 0.014, w: width * 0.008, tall: true }); x = width * 0.035; }
    while (x < width * (guards ? 0.96 : 1)) {
      const w = (0.6 + rng() * 2.4) * (width / 120);
      out.push({ x, w, tall: false });
      x += w + (width / 130) * (0.6 + rng() * 1.4);
    }
    if (guards) out.push({ x: width * 0.972, w: width * 0.008, tall: true }, { x: width * 0.986, w: width * 0.008, tall: true });
    return out;
  }, [seed, width, guards]);
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      {bars.map((b, i) => <rect key={i} x={b.x} y={0} width={b.w} height={b.tall ? height : height * 0.92} fill="#111" />)}
    </svg>
  );
}

export function BarcodeGraphic({ format, seed, scale }: { format: string; seed: string; scale: number }) {
  const square = format === "PKBarcodeFormatQR" || format === "PKBarcodeFormatAztec";
  if (format === "PKBarcodeFormatQR") return <QrGraphic seed={seed} size={104 * scale} />;
  if (format === "PKBarcodeFormatAztec") return <AztecGraphic seed={seed} size={104 * scale} />;
  if (format === "PKBarcodeFormatPDF417") return <Pdf417Graphic seed={seed} width={252 * scale} height={64 * scale} />;
  if (square) return <QrGraphic seed={seed} size={104 * scale} />;
  return <LinearGraphic seed={seed} width={252 * scale} height={52 * scale} guards={format === "PKBarcodeFormatEAN13"} />;
}

// ---------------------------------------------------------------------------
// Field blocks
// ---------------------------------------------------------------------------

function FieldBlock({
  field, s, labelColor, fgColor, labelSize = 9, valueSize = 13.5, align = "text-left", values, bold = false,
}: {
  field: AnyJson; s: number; labelColor: string; fgColor: string;
  labelSize?: number; valueSize?: number; align?: string; values?: Record<string, string | number>; bold?: boolean;
}) {
  const value = formatFieldValue(field, values);
  return (
    <div className={`${alignClass(field, align)} min-w-0`}>
      {field.label ? (
        <p className="uppercase leading-tight truncate" style={{ color: labelColor, fontSize: labelSize * s, letterSpacing: 0.6 * s }}>
          {field.label}
        </p>
      ) : null}
      <p className={`leading-tight truncate ${bold ? "font-semibold" : "font-medium"}`} style={{ color: fgColor, fontSize: valueSize * s }}>
        {value || "—"}
      </p>
    </div>
  );
}

interface RowItem {
  field: AnyJson;
  area: string; // "secondary" | "auxiliary"
  index: number; // index within that area's designer list
}

function TransitIcon({ transitType, color, size }: { transitType: string; color: string; size: number }) {
  const stroke = { stroke: color, strokeWidth: 1.6, fill: "none", strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      {transitType === "PKTransitTypeAir" ? (
        <path d="M21 15v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V8l-8 5v2l8-2.5V18l-2 1.5V21l3.5-1 3.5 1v-1.5L13 18v-5.5l8 2.5z" fill={color} stroke="none" />
      ) : transitType === "PKTransitTypeTrain" ? (
        <g {...stroke}><rect x="5" y="3" width="14" height="13" rx="3" /><path d="M5 11h14M9 19l-2 3M15 19l2 3M9.5 14.5h.01M14.5 14.5h.01" /></g>
      ) : transitType === "PKTransitTypeBus" ? (
        <g {...stroke}><rect x="4" y="3" width="16" height="14" rx="3" /><path d="M4 10h16M7.5 20v2M16.5 20v2M8 14h.01M16 14h.01" /></g>
      ) : transitType === "PKTransitTypeBoat" ? (
        <g {...stroke}><path d="M3 19s2.5-1.5 4.5-1.5S11 19 12 19s2.5-1.5 4.5-1.5S21 19 21 19M5 16l1.5-7h11L19 16M12 5v4" /></g>
      ) : (
        <g {...stroke}><path d="M4 12h16M14 6l6 6-6 6" /></g>
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// The pass
// ---------------------------------------------------------------------------

export function WalletPassPreview({
  passJson, images, values, face = "front", width = 320, voided, placeholders, onAreaClick, selected, blueprint,
}: WalletPassPreviewProps) {
  const s = width / 375;
  const style = styleOf(passJson);
  const section: AnyJson = passJson[style] || {};
  const bg = anyColorToHex(passJson.backgroundColor, "#1c1c1e");
  const fg = anyColorToHex(passJson.foregroundColor, "#ffffff");
  const label = anyColorToHex(passJson.labelColor, "#a6a6ab");

  const header: AnyJson[] = section.headerFields || [];
  const primary: AnyJson[] = section.primaryFields || [];
  const secondary: AnyJson[] = section.secondaryFields || [];
  const auxAll: AnyJson[] = section.auxiliaryFields || [];
  const aux0: RowItem[] = [];
  const aux1: RowItem[] = [];
  auxAll.forEach((f, i) => (f.row === 1 ? aux1 : aux0).push({ field: f, area: "auxiliary", index: i }));
  const secondaryItems: RowItem[] = secondary.map((f, i) => ({ field: f, area: "secondary", index: i }));
  const back: AnyJson[] = (section.backFields || []).filter((f: AnyJson) => !(f.key === "notification" && !f.value));

  const barcodes: AnyJson[] = Array.isArray(passJson.barcodes) ? passJson.barcodes : passJson.barcode ? [passJson.barcode] : [];
  const barcode = barcodes[0];
  // The barcode payload exactly as Wallet encodes it: the message with {{tokens}} filled
  // from `values`. A template preview with no values encodes the raw template string.
  const barcodeSeed = barcode ? interpolateTokens(String(barcode.message ?? ""), values) || "flowpass" : "";

  // The interactive overlay: a transparent layer ON TOP of the element. It owns
  // the click, shows a soft frame on hover, and a solid frame + name chip when
  // this exact element is the designer's selection. Because it's the topmost
  // child of the target (inset-0), images can't cover it and clipping can't
  // crop it. Parent must be `relative`.
  const Hit = ({ area, name, radius = 6 }: { area: string; name: string; radius?: number }) => {
    if (!onAreaClick) return null;
    const isSel = selected === area;
    return (
      <span
        role="button"
        aria-label={`Edit ${name}`}
        title={`Edit ${name}`}
        onClick={(e) => { e.stopPropagation(); onAreaClick(area); }}
        className={`absolute inset-0 z-30 cursor-pointer transition-opacity duration-100 ${isSel ? "opacity-100" : "opacity-0 hover:opacity-100"}`}
        style={{
          borderRadius: radius * s,
          border: isSel ? `${Math.max(1.5, 2 * s)}px solid #38bdf8` : `1px solid rgba(125, 211, 252, 0.9)`,
          background: isSel ? "rgba(56, 189, 248, 0.12)" : "rgba(56, 189, 248, 0.10)",
        }}
      >
        <span
          className={`absolute left-0 top-0 select-none whitespace-nowrap font-semibold text-white bg-sky-500 ${isSel ? "" : "opacity-90"}`}
          style={{ fontSize: 8 * s, padding: `${1.5 * s}px ${5 * s}px`, borderTopLeftRadius: radius * s, borderBottomRightRadius: 6 * s, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {name}
        </span>
      </span>
    );
  };

  // `inside` keeps the tag within the zone — used for zones flush against the
  // card's clipped edges, where an overhanging tag would be cropped.
  const BlueprintTag = ({ text, inside }: { text: string; inside?: boolean }) =>
    blueprint ? (
      <span
        className="absolute z-20 rounded-sm bg-sky-500/80 text-white font-semibold pointer-events-none select-none"
        style={{ fontSize: 7.5 * s, padding: `${1 * s}px ${4 * s}px`, top: inside ? 2 * s : -7 * s, left: 6 * s, letterSpacing: 0.4 }}
      >
        {text}
      </span>
    ) : null;

  const blueprintOutline = blueprint ? { boxShadow: "inset 0 0 0 1px rgba(56,189,248,0.45)" } : undefined;

  const FieldRow = ({ items, valueSize = 13.5, tag }: { items: RowItem[]; valueSize?: number; tag?: string }) => {
    if (!items.length && !blueprint) return null;
    return (
      <div
        className={`relative flex ${items.length > 1 ? "justify-between" : "justify-start"} items-start`}
        style={{ gap: 8 * s, paddingLeft: 14 * s, paddingRight: 14 * s, marginTop: 10 * s, minHeight: blueprint ? 24 * s : undefined, ...(blueprint && tag ? { boxShadow: "inset 0 0 0 1px rgba(56,189,248,0.3)" } : {}) }}
      >
        {tag ? <BlueprintTag text={tag} /> : null}
        {items.map(({ field, area, index }, i) => (
          <div key={field.key || `${area}${index}`} className="relative min-w-0" style={{ padding: `${2 * s}px ${4 * s}px`, margin: `-${2 * s}px -${4 * s}px` }}>
            <FieldBlock
              field={field}
              s={s}
              labelColor={label}
              fgColor={fg}
              values={values}
              valueSize={valueSize}
              align={items.length > 1 && i === items.length - 1 ? "text-right" : "text-left"}
            />
            <Hit area={`${area}#${index}`} name={field.label || field.key || `${area} ${index + 1}`} radius={5} />
          </div>
        ))}
      </div>
    );
  };

  const eventBackgroundMode = style === "eventTicket" && (images.background || (!images.strip && images.thumbnail));
  const hasStripArea = style === "storeCard" || style === "coupon" || (style === "eventTicket" && !eventBackgroundMode);
  // Apple's current docs put storeCard strips at 375×144 (same as coupons);
  // 123 was the legacy storeCard height and cropped modern 144-ratio art.
  const stripHeight = style === "eventTicket" ? 98 : 144;

  // ---- back face -----------------------------------------------------------
  if (face === "back") {
    return (
      <div
        className={`relative overflow-hidden shadow-xl ${onAreaClick ? "cursor-pointer" : ""}`}
        style={{ width, borderRadius: 12 * s, background: "#f2f2f7", minHeight: 420 * s }}
        onClick={onAreaClick ? () => onAreaClick("backface") : undefined}
      >
        <div className="flex items-center justify-between" style={{ padding: 14 * s }}>
          <div className="relative flex items-center min-w-0" style={{ gap: 8 * s }}>
            {images.icon ? (
              <img src={images.icon} alt="" className="object-cover" style={{ width: 28 * s, height: 28 * s, borderRadius: 6 * s }} />
            ) : (
              <div className="bg-slate-300" style={{ width: 28 * s, height: 28 * s, borderRadius: 6 * s }} />
            )}
            <p className="font-semibold text-slate-900 truncate" style={{ fontSize: 14 * s }}>{passJson.logoText || passJson.organizationName || "Pass"}</p>
            <Hit area="identity" name="Name & identity" />
          </div>
          <div className="flex items-center justify-center bg-slate-200 text-slate-500 shrink-0" style={{ width: 24 * s, height: 24 * s, borderRadius: 12 * s, fontSize: 12 * s }}>✕</div>
        </div>
        <div className="bg-white" style={{ marginLeft: 14 * s, marginRight: 14 * s, borderRadius: 10 * s, overflow: "hidden" }}>
          <div className="flex items-center justify-between" style={{ padding: 12 * s }}>
            <p className="text-slate-900" style={{ fontSize: 13 * s }}>Automatic Updates</p>
            <div className="bg-emerald-500 relative" style={{ width: 40 * s, height: 24 * s, borderRadius: 12 * s }}>
              <div className="absolute bg-white shadow" style={{ width: 20 * s, height: 20 * s, borderRadius: 10 * s, top: 2 * s, right: 2 * s }} />
            </div>
          </div>
        </div>
        <div className="relative bg-white" style={{ margin: 14 * s, borderRadius: 10 * s, ...(blueprint ? { boxShadow: "inset 0 0 0 1px rgba(56,189,248,0.45)" } : {}) }}>
          {blueprint ? <BlueprintTag text="Back fields" inside /> : null}
          {back.length === 0 ? (
            <div className="relative">
              <p className="text-slate-400" style={{ padding: 12 * s, fontSize: 12 * s }}>
                No back fields yet — add terms, contact info or links.
              </p>
              <Hit area="backface" name="Add back fields" radius={10} />
            </div>
          ) : (
            back.map((f, i) => (
              <div
                key={f.key || i}
                className="relative"
                style={{ padding: 12 * s, borderTop: i > 0 ? "1px solid #e5e5ea" : undefined }}
              >
                {(() => {
                  // Apple lays a back row out as LABEL LEFT / value right on one
                  // line when the value is short, and only stacks when it is long
                  // (terms, the how-it-works paragraph). It also keeps the label
                  // in the case it was authored in — Wallet never upper-cases it.
                  // The old render upper-cased every label and always stacked, so
                  // the preview looked nothing like the card being previewed.
                  const anchor = anchorFrom(f.attributedValue, values);
                  const text = anchor ? anchor.text : String(formatFieldValue(f, values));
                  const inline = !!f.label && text.length > 0 && text.length <= 28 && !text.includes("\n");
                  const valueEl = anchor ? (
                    <span className="text-sky-600">{anchor.text}</span>
                  ) : (
                    text
                      .split(/(https?:\/\/\S+)/g)
                      .map((part, j) => (/^https?:\/\//.test(part) ? <span key={j} className="text-sky-600 underline">{part}</span> : <span key={j}>{part}</span>))
                  );
                  if (inline) {
                    return (
                      <div className="flex items-baseline justify-between" style={{ gap: 10 * s }}>
                        <p className="text-slate-900 truncate" style={{ fontSize: 12.5 * s }}>{f.label}</p>
                        <p className="text-right shrink-0 text-slate-500" style={{ fontSize: 12.5 * s }}>{valueEl}</p>
                      </div>
                    );
                  }
                  return (
                    <>
                      {f.label ? <p className="text-slate-900" style={{ fontSize: 12.5 * s }}>{f.label}</p> : null}
                      {text ? (
                        <p className="text-slate-500 whitespace-pre-wrap break-words" style={{ fontSize: 12.5 * s, marginTop: 2 * s }}>{valueEl}</p>
                      ) : null}
                    </>
                  );
                })()}
                <Hit area={`back#${i}`} name={f.label || f.key || `Back field ${i + 1}`} radius={i === 0 ? 10 : 0} />
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ---- front face -----------------------------------------------------------
  return (
    <div
      className={`relative shadow-xl ${onAreaClick ? "cursor-pointer" : ""}`}
      style={{ width, borderRadius: 12 * s, backgroundColor: bg }}
      onClick={onAreaClick ? () => onAreaClick("pass") : undefined}
      title={onAreaClick ? "Edit design" : undefined}
    >
      {/* Clip layer (kept separate so overlays/tags near edges stay visible) */}
      <div className="relative overflow-hidden" style={{ borderRadius: 12 * s }}>
        {/* eventTicket background-image mode: blurred art behind everything */}
        {eventBackgroundMode && images.background ? (
          <>
            <img src={images.background} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ filter: `blur(${8 * s}px)`, transform: "scale(1.15)" }} />
            <div className="absolute inset-0" style={{ backgroundColor: bg, opacity: 0.35 }} />
          </>
        ) : null}

        <div className="relative">
          {/* Header row */}
          <div className="relative" style={{ paddingLeft: 14 * s, paddingRight: 14 * s, paddingTop: 12 * s, ...(blueprint ? { boxShadow: "inset 0 0 0 1px rgba(56,189,248,0.3)" } : {}) }}>
            {blueprint ? <BlueprintTag text="Logo · Header" inside /> : null}
            <div className="flex items-start justify-between" style={{ gap: 10 * s }}>
              <div className="flex items-center min-w-0" style={{ gap: 8 * s }}>
                <div className="relative shrink-0">
                  {images.logo ? (
                    <img src={images.logo} alt="" className="object-contain" style={{ maxHeight: 27 * s, maxWidth: 110 * s }} />
                  ) : placeholders ? (
                    <div className="border border-dashed flex items-center justify-center" style={{ borderColor: `${label}66`, width: 44 * s, height: 26 * s, borderRadius: 5 * s }}>
                      <span style={{ color: label, fontSize: 7 * s }}>LOGO</span>
                    </div>
                  ) : null}
                  {(images.logo || placeholders) ? <Hit area="logo" name="Logo image" radius={5} /> : null}
                </div>
                {passJson.logoText || placeholders ? (
                  <div className="relative min-w-0" style={{ padding: `${1 * s}px ${3 * s}px`, margin: `-${1 * s}px -${3 * s}px` }}>
                    {passJson.logoText ? (
                      <p className="font-semibold truncate" style={{ color: fg, fontSize: 15 * s }}>{passJson.logoText}</p>
                    ) : (
                      <p className="truncate" style={{ color: `${label}99`, fontSize: 13 * s }}>Logo text</p>
                    )}
                    <Hit area="logoText" name="Logo text" radius={4} />
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0" style={{ gap: 10 * s }}>
                {header.slice(0, 3).map((f, i) => (
                  <div key={f.key || i} className="relative" style={{ padding: `${2 * s}px ${4 * s}px`, margin: `-${2 * s}px -${4 * s}px` }}>
                    <FieldBlock field={f} s={s} labelColor={label} fgColor={fg} align="text-right" values={values} labelSize={8.5} valueSize={15} bold />
                    <Hit area={`header#${i}`} name={f.label || f.key || `Header ${i + 1}`} radius={5} />
                  </div>
                ))}
                {!header.length && placeholders ? (
                  <div className="relative border border-dashed flex items-center justify-center" style={{ borderColor: `${label}55`, width: 56 * s, height: 26 * s, borderRadius: 5 * s }}>
                    <span style={{ color: label, fontSize: 7 * s }}>HEADER</span>
                    <Hit area="header#0" name="Add a header field" radius={5} />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Primary area — per style */}
          {hasStripArea ? (
            <div className="relative" style={{ height: stripHeight * s, marginTop: 10 * s, ...blueprintOutline }}>
              {blueprint ? <BlueprintTag text={`Strip · Primary`} /> : null}
              {images.strip ? (
                <img src={images.strip} alt="" className="absolute inset-0 h-full w-full object-cover" />
              ) : placeholders ? (
                <div className="absolute inset-0 flex items-center justify-center border-y border-dashed" style={{ borderColor: `${label}44` }}>
                  <span style={{ color: label, fontSize: 9 * s }}>Strip image ({style === "eventTicket" ? "375×98" : "375×144"})</span>
                </div>
              ) : null}
              {/* Strip hit covers the zone; the primary text's own hit sits above it. */}
              <Hit area="strip" name="Strip image" radius={0} />
              {primary[0] || placeholders ? (
                <div
                  className="absolute z-40"
                  style={{ left: 14 * s, bottom: 6 * s, maxWidth: width - 28 * s, textShadow: images.strip ? "0 1px 4px rgba(0,0,0,0.45)" : undefined }}
                >
                  <div className="relative" style={{ padding: `${2 * s}px ${4 * s}px`, margin: `-${2 * s}px -${4 * s}px` }}>
                    {primary[0]?.label ? (
                      <p className="uppercase leading-tight" style={{ color: images.strip ? "#ffffff" : label, fontSize: 9.5 * s, letterSpacing: 0.6 * s }}>{primary[0].label}</p>
                    ) : null}
                    <p className="leading-tight truncate" style={{ color: images.strip ? "#ffffff" : fg, fontSize: (style === "coupon" ? 26 : 30) * s, fontWeight: 500 }}>
                      {primary[0] ? formatFieldValue(primary[0], values) || (placeholders ? "Primary value" : "") : placeholders ? "Primary value" : ""}
                    </p>
                    <Hit area="primary#0" name={primary[0]?.label || "Primary field"} radius={5} />
                  </div>
                </div>
              ) : null}
            </div>
          ) : style === "boardingPass" ? (
            <div className="relative flex items-center justify-between" style={{ paddingLeft: 14 * s, paddingRight: 14 * s, marginTop: 16 * s, gap: 8 * s, ...blueprintOutline }}>
              {blueprint ? <BlueprintTag text="Primary — journey" /> : null}
              <div className="relative min-w-0" style={{ padding: `${2 * s}px ${4 * s}px`, margin: `-${2 * s}px -${4 * s}px` }}>
                {primary[0]?.label ? <p className="uppercase" style={{ color: label, fontSize: 9.5 * s, letterSpacing: 0.6 * s }}>{primary[0].label}</p> : null}
                <p className="leading-none truncate" style={{ color: fg, fontSize: 40 * s, fontWeight: 300 }}>{primary[0] ? formatFieldValue(primary[0], values) || "···" : "···"}</p>
                <Hit area="primary#0" name={primary[0]?.label || "Origin"} radius={5} />
              </div>
              <div className="relative shrink-0" style={{ opacity: 0.9, padding: 3 * s, margin: -3 * s }}>
                <TransitIcon transitType={section.transitType || "PKTransitTypeAir"} color={fg} size={26 * s} />
                <Hit area="transit" name="Transit type" radius={6} />
              </div>
              <div className="relative min-w-0 text-right" style={{ padding: `${2 * s}px ${4 * s}px`, margin: `-${2 * s}px -${4 * s}px` }}>
                {primary[1]?.label ? <p className="uppercase" style={{ color: label, fontSize: 9.5 * s, letterSpacing: 0.6 * s }}>{primary[1].label}</p> : null}
                <p className="leading-none truncate" style={{ color: fg, fontSize: 40 * s, fontWeight: 300 }}>{primary[1] ? formatFieldValue(primary[1], values) || "···" : "···"}</p>
                <Hit area="primary#1" name={primary[1]?.label || "Destination"} radius={5} />
              </div>
            </div>
          ) : (
            // generic + eventTicket background mode: primary text beside a thumbnail
            <div className="relative flex items-start justify-between" style={{ paddingLeft: 14 * s, paddingRight: 14 * s, marginTop: 12 * s, gap: 10 * s, ...blueprintOutline }}>
              {blueprint ? <BlueprintTag text="Primary · Thumbnail" /> : null}
              <div className="relative min-w-0 flex-1" style={{ padding: `${2 * s}px ${4 * s}px`, margin: `-${2 * s}px -${4 * s}px` }}>
                {primary[0] ? (
                  <>
                    {primary[0].label ? <p className="uppercase" style={{ color: label, fontSize: 9.5 * s, letterSpacing: 0.6 * s }}>{primary[0].label}</p> : null}
                    <p className="leading-tight" style={{ color: fg, fontSize: 21 * s, fontWeight: 500 }}>
                      {formatFieldValue(primary[0], values) || (placeholders ? "Primary value" : "")}
                    </p>
                  </>
                ) : placeholders ? (
                  <p style={{ color: `${label}99`, fontSize: 16 * s }}>Primary value</p>
                ) : null}
                <Hit area="primary#0" name={primary[0]?.label || "Primary field"} radius={5} />
              </div>
              <div className="relative shrink-0">
                {/* Wallet scales the thumbnail to fit, never crops it: a square image shows whole (~70pt), a 2:3 portrait fills the height. */}
                {images.thumbnail ? (
                  <img src={images.thumbnail} alt="" className="object-contain" style={{ width: 70 * s, height: 78 * s, objectPosition: "right top" }} />
                ) : placeholders ? (
                  <div className="border border-dashed flex items-center justify-center" style={{ borderColor: `${label}66`, width: 70 * s, height: 78 * s, borderRadius: 6 * s }}>
                    <span style={{ color: label, fontSize: 7.5 * s }}>THUMB</span>
                  </div>
                ) : null}
                {(images.thumbnail || placeholders) ? <Hit area="thumbnail" name="Thumbnail image" radius={6} /> : null}
              </div>
            </div>
          )}

          {/* Secondary + auxiliary rows.
              Store cards & coupons combine both into ONE row (max 4).
              Boarding passes show auxiliary ABOVE secondary. Event tickets can
              carry a second auxiliary row (row: 1). */}
          {style === "storeCard" || style === "coupon" || style === "generic" ? (
            <FieldRow items={[...secondaryItems, ...aux0].slice(0, 4)} tag="Secondary + Auxiliary" />
          ) : style === "boardingPass" ? (
            <>
              <FieldRow items={aux0.slice(0, 5)} tag="Auxiliary" />
              <FieldRow items={secondaryItems.slice(0, 4)} tag="Secondary" />
            </>
          ) : (
            <>
              <FieldRow items={secondaryItems.slice(0, 4)} tag="Secondary" />
              <FieldRow items={aux0.slice(0, 4)} tag="Auxiliary" />
              {aux1.length ? <FieldRow items={aux1.slice(0, 4)} tag="Auxiliary · row 2" /> : null}
            </>
          )}

          {/* Boarding pass footer image */}
          {style === "boardingPass" && (images.footer || placeholders) ? (
            <div className="relative" style={{ marginTop: 10 * s, paddingLeft: 14 * s, paddingRight: 14 * s }}>
              {blueprint ? <BlueprintTag text="Footer" /> : null}
              {images.footer ? (
                <img src={images.footer} alt="" className="w-full object-contain" style={{ maxHeight: 18 * s }} />
              ) : (
                <div className="border border-dashed flex items-center justify-center" style={{ borderColor: `${label}44`, height: 14 * s, borderRadius: 3 * s }}>
                  <span style={{ color: label, fontSize: 7 * s }}>FOOTER (286×15)</span>
                </div>
              )}
              <Hit area="footer" name="Footer image" radius={3} />
            </div>
          ) : null}

          {/* Barcode plate */}
          <div className="relative flex justify-center" style={{ paddingTop: 14 * s, paddingBottom: 14 * s, ...(blueprint ? { boxShadow: "inset 0 0 0 1px rgba(56,189,248,0.3)" } : {}) }}>
            {blueprint ? <BlueprintTag text="Barcode" /> : null}
            {barcode ? (
              <div className="relative bg-white flex flex-col items-center" style={{ borderRadius: 8 * s, padding: 8 * s, maxWidth: width - 28 * s }}>
                <BarcodeGraphic format={String(barcode.format)} seed={barcodeSeed} scale={s} />
                {barcode.altText ? (
                  <p className="text-slate-700" style={{ fontSize: 10 * s, marginTop: 4 * s }}>
                    {values ? String(barcode.altText).replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (m: string, k: string) => (values[k] !== undefined ? String(values[k]) : m)) : barcode.altText}
                  </p>
                ) : null}
                <Hit area="barcode" name="Barcode" radius={8} />
              </div>
            ) : placeholders ? (
              <div className="relative border border-dashed flex items-center justify-center" style={{ borderColor: `${label}55`, borderRadius: 8 * s, width: 130 * s, height: 60 * s }}>
                <span style={{ color: label, fontSize: 9 * s }}>No barcode</span>
                <Hit area="barcode" name="Barcode" radius={8} />
              </div>
            ) : null}
          </div>
        </div>

        {/* Voided overlay */}
        {voided ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/45">
            <p className="font-bold text-white/80 rotate-[-18deg] tracking-[0.35em] border-4 border-white/70 px-4 py-1 rounded-lg" style={{ fontSize: 30 * s }}>VOID</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// iPhone shell + lock screen
// ---------------------------------------------------------------------------

function StatusBar({ light }: { light?: boolean }) {
  const c = light ? "#fff" : "#0a0a0a";
  return (
    <div className="flex items-center justify-between px-7 pt-3.5 pb-1 select-none">
      <span className="text-[13px] font-semibold" style={{ color: c }}>9:41</span>
      <div className="flex items-center gap-1.5">
        <svg width="17" height="11" viewBox="0 0 17 11" fill={c}><rect x="0" y="7" width="3" height="4" rx="0.8" /><rect x="4.5" y="5" width="3" height="6" rx="0.8" /><rect x="9" y="2.5" width="3" height="8.5" rx="0.8" /><rect x="13.5" y="0" width="3" height="11" rx="0.8" /></svg>
        <svg width="16" height="11" viewBox="0 0 16 11" fill={c}><path d="M8 9.6a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6zM8 6.2c1.5 0 2.9.6 3.9 1.6l-1.3 1.3A3.6 3.6 0 0 0 8 8.1c-1 0-1.9.4-2.6 1l-1.3-1.3a5.5 5.5 0 0 1 3.9-1.6zM8 2.8c2.4 0 4.7 1 6.3 2.6l-1.3 1.3A7.1 7.1 0 0 0 8 4.7c-1.9 0-3.7.8-5 2l-1.3-1.3A9 9 0 0 1 8 2.8z" transform="translate(0 -2)" /></svg>
        <svg width="25" height="12" viewBox="0 0 25 12"><rect x="0.5" y="0.5" width="21" height="11" rx="3" fill="none" stroke={c} strokeOpacity="0.4" /><rect x="2" y="2" width="15" height="8" rx="1.6" fill={c} /><rect x="22.5" y="3.8" width="2" height="4.4" rx="1" fill={c} fillOpacity="0.4" /></svg>
      </div>
    </div>
  );
}

export function IPhoneShell({
  children, screenClassName = "", light, header, footerHint,
}: {
  children: React.ReactNode;
  screenClassName?: string;
  light?: boolean;
  header?: React.ReactNode;
  footerHint?: string;
}) {
  return (
    <div className="rounded-[52px] bg-black p-[10px] shadow-2xl shadow-black/40 inline-block">
      <div className={`relative rounded-[42px] overflow-hidden ${screenClassName}`} style={{ width: 348 }}>
        {/* Dynamic Island */}
        <div className="absolute left-1/2 -translate-x-1/2 top-2.5 w-[106px] h-[30px] rounded-full bg-black z-30" />
        <StatusBar light={light} />
        {header}
        <div className="relative z-10">{children}</div>
        {footerHint ? <p className={`text-center text-[10px] pb-2 ${light ? "text-white/50" : "text-slate-400"}`}>{footerHint}</p> : null}
        {/* Home indicator */}
        <div className="flex justify-center pb-2 pt-1">
          <div className={`w-32 h-[5px] rounded-full ${light ? "bg-white/70" : "bg-black/70"}`} />
        </div>
      </div>
    </div>
  );
}

export function LockScreenNotification({
  iconUrl, title, message, orgName,
}: {
  iconUrl?: string; title: string; message: string; orgName: string;
}) {
  const now = new Date();
  return (
    <IPhoneShell light screenClassName="bg-gradient-to-b from-indigo-950 via-slate-900 to-rose-950">
      <div className="pt-8 pb-3 text-center select-none">
        <p className="text-white/80 text-[15px] font-medium">
          {now.toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long" })}
        </p>
        <p className="text-white font-semibold leading-none mt-1" style={{ fontSize: 68, fontWeight: 600 }}>
          {now.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false })}
        </p>
      </div>
      <div className="px-3 pb-24 pt-4">
        <div className="rounded-3xl bg-white/85 backdrop-blur-xl p-3 shadow-lg">
          <div className="flex items-start gap-2.5">
            {iconUrl ? (
              <img src={iconUrl} alt="" className="w-9 h-9 rounded-[9px] object-cover shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-[9px] shrink-0 bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                <span className="text-white text-[15px]">🎟</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-[13px] font-semibold text-slate-900 truncate">{title || orgName}</p>
                <p className="text-[11px] text-slate-500 shrink-0">now</p>
              </div>
              <p className="text-[13px] text-slate-800 leading-snug break-words">{message}</p>
            </div>
          </div>
        </div>
      </div>
    </IPhoneShell>
  );
}
